import React, { useEffect, useRef, useState } from "react";

import { CheckCircle2, Loader2 } from "lucide-react";

import BriefingRunCard from "./BriefingRunCard";
import { FLOW } from "./flowCopy";
import PendingDraftNote from "./PendingDraftNote";
import { PlanCard, isViewable } from "./PlanCard";
import { UPLOAD_DELETE_ENABLED, shortDate } from "./shared";
import { UploadRejectedCard } from "./UploadRejectedCard";
import UploadStatusDialog from "./UploadStatusDialog";
import VersionHistory, { type UnusedUpload } from "./VersionHistory";
import DocumentDropzone from "../../Components/DocumentDropzone";
import {
  apiErrorMessage,
  uploadRejection,
  useAccountPlanActions,
  useMyPlanActivity,
} from "../../Hooks/useAccountPlan";
// A pure parser (no API call): imported from the slice so hook mocks needn't carry it.
import { draftPendingOf as draftPending } from "../../Store/Reducers/AccountPlanSlice";
import type {
  PendingDraft,
  PlanFile,
  PlanRejectedUpload,
  ReviewSummary,
  UploadRejection,
} from "../../Types/AccountPlan";

// Mirrors backend ACCOUNT_PLAN_MAX_BYTES and plan_parsing SUPPORTED.
const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT = ".pptx,.pdf";
// Only an upload that couldn't be read, didn't pass the plan checks, or is flagged and
// not yet OK'd can be deleted (VAL-26, VAL-22).
const DELETABLE: string[] = ["failed", "rejected", "needs_review"];

interface Props {
  accountId: string;
  files: PlanFile[];
  currentFileId: string | null;
  /**
   * The version sponsors read (overview `sponsor_file_id`). A published version
   * they still read is live, not superseded, even under a newer upload.
   */
  sponsorFileId?: string | null;
  /** AE uploads; CSM reads. */
  canEdit: boolean;
  /** The account's own AE, not an admin acting as one: only they delete (2026-10-31). */
  canDelete?: boolean;
  /** "{name} (AE)" for the read-only subtitle. */
  aeLabel: string;
  onChanged: () => void;
  onOpenViewer: (fileId: string) => void;
  /** Wraps the dropzone so "Upload plan" can scroll to it. */
  dropzoneRef?: React.Ref<HTMLDivElement>;
  /**
   * Deep link `focus=problem` (the panel's "Go to the problem"): open Rejected
   * uploads, expand the newest one, outline it and scroll it into view.
   */
  focusProblem?: boolean;
  /** The published briefing (read-only), from the published version's row. */
  onOpenPublished?: () => void;
  /** The draft briefing (Review briefing), from the current version's row. */
  onOpenDraft?: () => void;
  /** Review summaries the page has (current / published), for "x of y verified". */
  reviews?: (ReviewSummary | null | undefined)[];
  /** Pre-parse rejections, last 30 days (overview `rejected_uploads`, Phase 3). */
  rejectedUploads?: PlanRejectedUpload[];
  /**
   * A confirmed draft that isn't published (overview `pending_draft`, rule
   * 2026-10-30): the drop zone gives way to a note and "Replace this draft".
   */
  pendingDraft?: PendingDraft | null;
  /** Open "Replace this draft"'s confirm at once (deep link `replace=1`). */
  startReplace?: boolean;
  /** Reopen this upload's progress dialog (deep link `progress=<file id>`, the pill). */
  reopenProgress?: string | null;
  /** Called once it's open, so the link is dropped and a refresh doesn't reopen it. */
  onProgressReopened?: () => void;
  /** "Links to the plan file", shown under the drop zone and hidden with it. */
  linksSlot?: React.ReactNode;
}

interface Outcome {
  text: string;
  cls: string;
  /** A short line under the chip. */
  note?: string;
}

/**
 * What became of each plan version on the sponsor side (mock `versionBriefingOutcome`).
 * isSponsors: whether sponsors read this version; when the overview doesn't say
 * (undefined), the current version is taken to be theirs.
 */
export const briefingOutcome = (
  f: PlanFile,
  isCurrent: boolean,
  isSponsors?: boolean
): Outcome => {
  if (f.published) {
    const at = shortDate(f.published.at);
    if (isSponsors ?? isCurrent)
      return {
        text: `Published ${at}`,
        cls: "bg-emerald-100 text-emerald-800",
        // Under a newer upload, say why this older version is still green.
        note: isCurrent ? undefined : "Sponsors read this",
      };
    return {
      text: `Published ${at} · superseded`,
      cls: "bg-gray-100 text-gray-600",
    };
  }
  // Only the current version is reviewed; an older version's unpublished
  // draft was abandoned when a newer plan landed (mock: `f.current`).
  if (!isCurrent) return { text: "—", cls: "text-gray-400" };
  if (f.review_status === "draft" || f.review_status === "in_review")
    return { text: "Draft under review", cls: "bg-amber-100 text-amber-800" };
  if ((!f.review_status || f.review_status === "none") && isViewable(f))
    // Confirm plan (Phase 2): nothing is written until the AE confirms the version.
    return f.confirmed === false
      ? { text: FLOW.waitingToConfirm, cls: "bg-amber-100 text-amber-800" }
      : {
          text: "Briefing not generated yet",
          cls: "bg-gray-100 text-gray-600",
        };
  return { text: "—", cls: "text-gray-400" };
};

const PlanVersionsCard: React.FC<Props> = ({
  accountId,
  files,
  currentFileId,
  sponsorFileId,
  canEdit,
  canDelete = false,
  aeLabel,
  onChanged,
  onOpenViewer,
  dropzoneRef,
  focusProblem,
  onOpenPublished,
  onOpenDraft,
  reviews,
  rejectedUploads,
  pendingDraft,
  startReplace = false,
  reopenProgress = null,
  onProgressReopened,
  linksSlot,
}) => {
  const {
    uploadPlanFile,
    dismissPlanRejections,
    withdrawPlanUpload,
    retryPlanUpload,
    overridePlanVersion,
    deletePlanVersion,
    restorePlanVersion,
    deleteUnusedUpload,
    deleteRejection,
  } = useAccountPlanActions();
  const { kick, markSeen } = useMyPlanActivity(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Phase 3: an upload refused before reading, shown where the upload happened.
  // The File stays here so "Move it here" can send the very same file again.
  const [rejection, setRejection] = useState<{
    info: UploadRejection;
    file: File;
  } | null>(null);
  const [rejectionBusy, setRejectionBusy] = useState(false);
  const [rejectionError, setRejectionError] = useState<string | null>(null);
  // A tab opened before the draft was confirmed: the server refused the upload
  // (409 draft_pending). Its message and draft stand in until the next refresh.
  const [staleDraft, setStaleDraft] = useState<{
    message: string;
    pending: PendingDraft | null;
  } | null>(null);
  const blockingDraft = pendingDraft || staleDraft?.pending || null;
  const dropAreaRef = useRef<HTMLDivElement>(null);
  const processing = files.find(f => f.status === "processing") || null;
  /** A newer upload that wasn't discarded: confirm/undo on `f` would be refused (B08). */
  const newerKept = (f: PlanFile) =>
    files.some(o => o.version > f.version && !o.discarded);
  // The upload dialog follows one version (QUE-03): the one just uploaded or retried.
  const [dialog, setDialog] = useState<{ id: string; name: string } | null>(
    null
  );
  const [actionBusy, setActionBusy] = useState(false);
  // The version whose Delete is waiting for a second click.
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const dialogFile = dialog
    ? files.find(f => f.id === dialog.id) || null
    : null;

  // The newest upload, while it is read and until the AE confirms it, shows its
  // progress and checks on the page itself (Sushant, 2026-10-31: the pop-up only
  // repeated the page). Problems keep their own cards and "See the checks".
  const newest = files
    .filter(f => !f.discarded)
    .reduce<PlanFile | null>(
      (a, f) => (!a || f.version > a.version ? f : a),
      null
    );
  const live =
    processing ||
    (newest &&
    (newest.status === "ready" || newest.status === "partial") &&
    newest.confirmed === false
      ? newest
      : null);
  const liveRef = useRef<HTMLDivElement>(null);
  // The newest upload's progress is a pop-up again (Sushant, 2026-11-02, reversing
  // 2026-10-31): it opens on upload, and once closed a strip on the page (and the
  // activity pill) brings it back.
  const [liveOpen, setLiveOpen] = useState(false);
  useEffect(() => {
    if (!live) setLiveOpen(false);
  }, [live]);
  // An uploaded file that isn't confirmed yet hides the drop zone: uploading again
  // is rarely what the AE wants then, so it takes a deliberate click.
  const newestUnconfirmed =
    newest &&
    newest.confirmed === false &&
    ["processing", "ready", "partial", "needs_review"].includes(newest.status)
      ? newest
      : null;
  const [showDropzone, setShowDropzone] = useState(false);
  useEffect(() => {
    if (!newestUnconfirmed) setShowDropzone(false);
  }, [newestUnconfirmed]);
  // The newest confirmed version's briefing run, until it is published (product
  // review 2026-11-01): the checklist while it is written, then what it produced.
  const briefingRun = live
    ? null
    : (files
        .filter(
          f =>
            !f.discarded &&
            f.confirmed !== false &&
            !f.published &&
            (f.sponsor_view_status === "processing" ||
              f.sponsor_view_status === "ready")
        )
        .sort((a, b) => b.version - a.version)[0] ?? null);

  // The processing pill (PlanActivityTray) links here: reopen the upload's
  // progress, then drop the link so a refresh doesn't open it again.
  useEffect(() => {
    if (!reopenProgress) return;
    if (!files.some(x => x.id === reopenProgress)) return;
    if (live?.id === reopenProgress) setLiveOpen(true);
    else
      liveRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    onProgressReopened?.();
  }, [reopenProgress, files, live?.id, onProgressReopened]);

  // Seen in the dialog: no pop-up for the same outcome a moment later.
  useEffect(() => {
    if (dialogFile && dialogFile.status !== "processing")
      markSeen(dialogFile.id, "read");
  }, [dialogFile, markSeen]);

  const upload = async (
    file: File,
    moveFrom?: string | null,
    replaceDraft = false
  ) => {
    setUploading(file.name);
    setUploadError(null);
    setRejectionError(null);
    try {
      await uploadPlanFile(
        accountId,
        file,
        moveFrom,
        replaceDraft || undefined
      );
      setRejection(null);
      setStaleDraft(null);
      // Its progress opens in a pop-up as soon as the next poll brings it back.
      setLiveOpen(true);
      setShowDropzone(false);
      onChanged();
      kick(); // the header chip and pop-ups pick it up straight away
    } catch (err) {
      // Refused before reading (duplicate, type, password, damaged, pages…): a
      // Rejected card in place of the drop zone. The server recorded it, so the
      // history and the status pick it up on refresh.
      const info = uploadRejection(err);
      const pendingRefusal = draftPending(err);
      if (pendingRefusal) {
        setRejection(null);
        setStaleDraft({
          message: pendingRefusal.message,
          pending: pendingRefusal.pending_draft,
        });
        onChanged();
      } else if (info) {
        setRejection({ info, file });
        onChanged();
      } else if (moveFrom) {
        // The move itself failed: keep the card, say why under it.
        setRejectionError(apiErrorMessage(err, "Couldn't move the file here."));
      } else {
        setRejection(null);
        setUploadError(
          apiErrorMessage(err, "Upload failed. Please try again.")
        );
      }
    } finally {
      setUploading(null);
    }
  };

  const dismissRejection = async () => {
    setRejectionBusy(true);
    setRejectionError(null);
    try {
      await dismissPlanRejections(accountId);
      setRejection(null);
      onChanged();
    } catch (err) {
      const message = apiErrorMessage(err, "Couldn't dismiss it.");
      // From the card: under the card. From the history row: above the history.
      if (rejection) setRejectionError(message);
      else setRowError(message);
    } finally {
      setRejectionBusy(false);
    }
  };

  /** "Upload a different file": back to the drop zone, with the file picker open. */
  const pickAnother = () => {
    setRejection(null);
    setRejectionError(null);
    window.setTimeout(() => {
      dropAreaRef.current
        ?.querySelector<HTMLInputElement>("input[type='file']")
        ?.click();
    }, 0);
  };

  const closeDialog = () => {
    setDialog(null);
    setActionError(null);
  };

  const withdraw = async (id = dialog?.id) => {
    if (!id) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await withdrawPlanUpload(accountId, id);
      closeDialog();
      onChanged();
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't withdraw the upload."));
      onChanged();
    } finally {
      setActionBusy(false);
    }
  };

  /** Run one of the plan-check actions on the open version, then refresh. */
  const act = async (
    fn: () => Promise<unknown>,
    fallback: string,
    close = false,
    after?: () => void
  ) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await fn();
      if (close) closeDialog();
      onChanged();
      after?.();
    } catch (err) {
      setActionError(apiErrorMessage(err, fallback));
    } finally {
      setActionBusy(false);
    }
  };

  const retry = async (fileId: string) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await retryPlanUpload(accountId, fileId);
      // Read again: the page's progress card takes over from the pop-up.
      closeDialog();
      onChanged();
      kick();
    } catch (err) {
      const message = apiErrorMessage(err, "Couldn't try again.");
      if (dialog) setActionError(message);
      else setUploadError(message);
    } finally {
      setActionBusy(false);
    }
  };

  const ACT_BTN =
    "inline-flex h-8 items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50";
  const PRIMARY_ACT_BTN =
    "inline-flex h-8 items-center whitespace-nowrap rounded-lg bg-[#9333EA] px-3 text-xs font-semibold text-white hover:bg-[#7E22CE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50";

  /**
   * What can be done today about the newest upload when it stopped (the history
   * shows these only for that one): try reading it again, OK a flagged plan, or
   * say a rejected one is the right plan (with a reason, in the upload dialog).
   */
  const problemActions = (f: PlanFile) => {
    const fails = (f.validation?.checks || []).filter(c => c.status === "fail");
    const overridable =
      f.status === "rejected" &&
      fails.length > 0 &&
      fails.every(c => c.overridable);
    return (
      <>
        {/* Phase 3: only when reading it again can help (not scanned, password,
            damaged, no pages) and the stored file still exists. */}
        {canEdit &&
          f.status === "failed" &&
          f.retryable !== false &&
          !f.purged &&
          !processing && (
            <button
              type="button"
              onClick={() => void retry(f.id)}
              disabled={actionBusy}
              className={PRIMARY_ACT_BTN}
            >
              Try again
            </button>
          )}
        {canEdit && overridable && (
          <button
            type="button"
            onClick={() => setDialog({ id: f.id, name: f.filename })}
            className={PRIMARY_ACT_BTN}
          >
            It&apos;s the right plan
          </button>
        )}
        {(f.status === "rejected" || f.status === "needs_review") && (
          <button
            type="button"
            onClick={() => setDialog({ id: f.id, name: f.filename })}
            className={ACT_BTN}
          >
            See the checks
          </button>
        )}
        {isViewable(f) && !f.purged && (
          <button
            type="button"
            onClick={() => onOpenViewer(f.id)}
            className={ACT_BTN}
          >
            {FLOW.viewSource}
          </button>
        )}
        {/* "Discard it now" was replaced by Delete (Sushant, 2026-10-31): the row's
            own Delete lets a held upload go at once (unusedActions). */}
        {canEdit &&
          UPLOAD_DELETE_ENABLED &&
          DELETABLE.includes(f.status) &&
          (confirmDel === f.id ? (
            <span className="inline-flex items-center gap-2 text-xs">
              <span className="text-gray-600">
                Delete this upload and its stored file?
              </span>
              <button
                type="button"
                disabled={actionBusy}
                onClick={async () => {
                  setActionBusy(true);
                  setRowError(null);
                  try {
                    await deletePlanVersion(accountId, f.id);
                    setConfirmDel(null);
                    onChanged();
                  } catch (err) {
                    setRowError(
                      apiErrorMessage(err, "Couldn't delete this upload.")
                    );
                  } finally {
                    setActionBusy(false);
                  }
                }}
                className="font-semibold text-rose-700 hover:underline disabled:opacity-50"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDel(null)}
                className="text-gray-500 hover:underline"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                setRowError(null);
                setConfirmDel(f.id);
              }}
              className={ACT_BTN}
            >
              Delete
            </button>
          ))}
      </>
    );
  };

  // F-UF-F03: a flagged upload is confirmed on Confirm plan, where the AE ticks
  // "I've checked this…"; never in one click from here. On the row's own line.
  const problemPrimary = (f: PlanFile) =>
    canEdit && f.status === "needs_review" && !newerKept(f) ? (
      <span className="pr-3">
        <button
          type="button"
          onClick={() => onOpenViewer(f.id)}
          className={PRIMARY_ACT_BTN}
        >
          {FLOW.goToConfirm}
        </button>
      </span>
    ) : null;

  /** "Undo" on a discarded upload while it is kept (7 days): back to Confirm plan. */
  const discardActions = (f: PlanFile) => {
    const until = f.discarded?.undo_until;
    // A purged upload's stored file is gone: nothing to restore (the history row
    // says "Deleted after 7 days"). A file moved to another account lives there
    // now (F-UF-F05), and only the newest upload can come back: an older one under
    // a newer, kept upload would take a plan number out of order (B08).
    if (
      !canEdit ||
      f.purged ||
      f.discarded?.moved_to ||
      newerKept(f) ||
      !until ||
      Date.now() >= new Date(until).getTime()
    )
      return null;
    return (
      <button
        type="button"
        disabled={actionBusy}
        title={`Kept until ${shortDate(until)}`}
        onClick={async () => {
          setActionBusy(true);
          setRowError(null);
          try {
            await restorePlanVersion(accountId, f.id);
            onChanged();
            onOpenViewer(f.id);
          } catch (err) {
            setRowError(apiErrorMessage(err, "Couldn't undo the discard."));
          } finally {
            setActionBusy(false);
          }
        }}
        className={ACT_BTN}
      >
        {FLOW.undo}
      </button>
    );
  };

  /**
   * "Delete" on any upload that didn't become a plan (Sushant, 2026-10-31): gone at
   * once, after one confirm; left alone, the server deletes it on its date.
   */
  const unusedActions = (u: UnusedUpload) => {
    const key = u.kind === "file" ? `file:${u.f.id}` : `pre:${u.r.id}`;
    if (confirmDel !== key)
      return (
        <button
          type="button"
          onClick={() => {
            setRowError(null);
            setConfirmDel(key);
          }}
          className={ACT_BTN}
        >
          {FLOW.deleteUnused}
        </button>
      );
    return (
      <span
        className="inline-flex items-center gap-2 text-xs"
        data-testid="delete-unused-confirm"
      >
        <span className="text-gray-600">{FLOW.deleteUnusedAsk}</span>
        <button
          type="button"
          disabled={actionBusy}
          onClick={async () => {
            setActionBusy(true);
            setRowError(null);
            try {
              if (u.kind === "file")
                await deleteUnusedUpload(accountId, u.f.id);
              else if (u.r.id) await deleteRejection(accountId, u.r.id);
              setConfirmDel(null);
              onChanged();
            } catch (err) {
              setRowError(apiErrorMessage(err, "Couldn't delete it."));
            } finally {
              setActionBusy(false);
            }
          }}
          className="font-semibold text-rose-700 hover:underline disabled:opacity-50"
        >
          {FLOW.deleteUnused}
        </button>
        <button
          type="button"
          onClick={() => setConfirmDel(null)}
          className="text-gray-500 hover:underline"
        >
          Cancel
        </button>
      </span>
    );
  };

  return (
    <PlanCard
      title={
        canEdit
          ? "Uploads and versions"
          : "Uploads and versions (read-only for CSM)"
      }
      subtitle={
        canEdit
          ? "Upload a refreshed PPTX/PDF whenever the source plan changes, then confirm it to start the draft briefing."
          : `${aeLabel} owns the plan. You confirm the operational sections in Review draft.`
      }
    >
      {canEdit && (
        <div ref={dropzoneRef} className="mb-4">
          <div ref={dropAreaRef}>
            {uploading ? (
              <div
                className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-purple-200 bg-purple-50/40 px-6 py-8 text-sm text-gray-700"
                role="status"
              >
                <Loader2
                  className="h-4 w-4 animate-spin text-[#974ae4]"
                  aria-hidden
                />
                Uploading {uploading}…
              </div>
            ) : rejection ? (
              <UploadRejectedCard
                rejection={rejection.info}
                filename={rejection.file.name}
                busy={rejectionBusy}
                error={rejectionError}
                onMove={moveFrom => void upload(rejection.file, moveFrom)}
                onUploadDifferent={pickAnother}
                onDismiss={() => void dismissRejection()}
                onOpenExisting={id => {
                  setRejection(null);
                  onOpenViewer(id);
                }}
              />
            ) : blockingDraft && !processing ? (
              <PendingDraftNote
                pendingDraft={blockingDraft}
                accept={ACCEPT}
                startConfirming={startReplace}
                serverMessage={staleDraft?.message}
                onReplace={f => void upload(f, null, true)}
              />
            ) : newestUnconfirmed && !showDropzone ? (
              processing ? null : (
                <p
                  data-testid="upload-again-link"
                  className="text-sm text-gray-600"
                >
                  Wrong file?{" "}
                  <button
                    type="button"
                    onClick={() => setShowDropzone(true)}
                    className="font-medium text-[#974ae4] hover:underline"
                  >
                    Upload a different one
                  </button>
                </p>
              )
            ) : (
              <>
                {staleDraft && (
                  <p role="alert" className="mb-2 text-sm text-amber-900">
                    {staleDraft.message}
                  </p>
                )}
                {/* One upload at a time per account (spec UP-04): the server refuses a
                  second one (409) while a version is still being read. */}
                <DocumentDropzone
                  accept={ACCEPT}
                  maxBytes={MAX_BYTES}
                  onFileSelected={f => void upload(f)}
                  disabled={!!processing}
                  hint="PowerPoint (.pptx) or PDF, up to 50 MB · stored in ExecBridge, isolated to your organization"
                />
                {linksSlot && <div className="mt-4">{linksSlot}</div>}
                {processing && (
                  <p
                    data-testid="upload-blocked"
                    className="mt-2 text-sm text-gray-600"
                  >
                    The new upload is still being read. You can upload another
                    version once it&apos;s ready.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {uploadError && (
        <p role="alert" className="mb-3 text-sm text-red-600">
          {uploadError}
        </p>
      )}

      {live && (
        <div ref={liveRef} className="mb-4 scroll-mt-4">
          <div
            data-testid="upload-status-strip"
            role="status"
            className="flex items-center gap-3 rounded-lg border border-purple-200 bg-purple-50/40 px-4 py-3"
          >
            {live.status === "processing" ? (
              <Loader2
                className="h-4 w-4 shrink-0 animate-spin text-[#974ae4]"
                aria-hidden
              />
            ) : (
              <CheckCircle2
                className="h-4 w-4 shrink-0 text-emerald-600"
                aria-hidden
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900">
                {live.status !== "processing"
                  ? "Your upload is ready to confirm"
                  : live.queue?.read?.state === "queued"
                    ? FLOW.queuedForParsing
                    : FLOW.parsing}
              </div>
              <div className="truncate text-xs text-gray-500">
                {live.filename}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLiveOpen(true)}
              className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {live.status === "processing" ? "Show progress" : "Show details"}
            </button>
          </div>
          <UploadStatusDialog
            open={liveOpen}
            file={live}
            filename={live.filename}
            canEdit={canEdit}
            busy={actionBusy}
            error={dialog ? null : actionError}
            onClose={() => setLiveOpen(false)}
            onWithdraw={() => void withdraw(live.id)}
            onRetry={() => void retry(live.id)}
            onOpenPlan={() => {
              setLiveOpen(false);
              onOpenViewer(live.id);
            }}
          />
        </div>
      )}

      {briefingRun && (
        <div className="mb-4">
          <BriefingRunCard file={briefingRun} onOpenDraft={onOpenDraft} />
        </div>
      )}

      <UploadStatusDialog
        open={!!dialog}
        file={dialogFile}
        checksOnly={dialogFile?.status === "needs_review"}
        filename={dialog?.name || ""}
        canEdit={canEdit}
        busy={actionBusy}
        error={actionError}
        onClose={closeDialog}
        onWithdraw={() => void withdraw()}
        onRetry={() => dialog && void retry(dialog.id)}
        onOpenPlan={() => {
          if (dialog) onOpenViewer(dialog.id);
          closeDialog();
        }}
        // F-UF-F03: no one-click confirm of a flagged upload here. "Open plan"
        // leads to Confirm plan, where the AE ticks "I've checked this…".
        onOverride={reason =>
          dialog &&
          void act(
            () => overridePlanVersion(accountId, dialog.id, reason),
            "Couldn't use this version.",
            true,
            onOpenDraft
          )
        }
        onDiscard={
          UPLOAD_DELETE_ENABLED
            ? () =>
                dialog &&
                void act(
                  () => deletePlanVersion(accountId, dialog.id),
                  "Couldn't remove this version.",
                  true
                )
            : undefined
        }
      />

      {rowError && (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {rowError}
        </p>
      )}
      <VersionHistory
        files={files}
        currentFileId={currentFileId}
        sponsorFileId={sponsorFileId}
        reviews={reviews}
        focusProblem={focusProblem}
        onOpenViewer={onOpenViewer}
        onOpenPublished={onOpenPublished}
        onOpenDraft={onOpenDraft}
        problemActions={problemActions}
        problemPrimary={problemPrimary}
        discardActions={discardActions}
        rejectionIsProblem={canEdit}
        viewerConfirms={canEdit}
        rejections={rejectedUploads}
        unusedActions={canDelete ? unusedActions : undefined}
        rejectionActions={
          canEdit
            ? () => (
                <button
                  type="button"
                  disabled={rejectionBusy}
                  onClick={() => void dismissRejection()}
                  className={ACT_BTN}
                >
                  {FLOW.dismiss}
                </button>
              )
            : undefined
        }
      />
    </PlanCard>
  );
};

export default PlanVersionsCard;
